import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

export type DebugLogCategory = 'api' | 'pusher' | 'error' | 'message' | 'state';
export type DebugLogLevel = 'info' | 'warn' | 'error';

export type DebugLogEntry = {
  id: string;
  timestamp: Date;
  category: DebugLogCategory;
  level: DebugLogLevel;
  title: string;
  summary?: string;
  details?: unknown;
};

export type DebugContextInfo = {
  tripId: string;
  trip?: {
    id: number;
    finalDestination?: string;
    purpose?: string;
    status?: number;
    draft?: boolean;
  };
  user?: {
    id: number;
    name: string;
    email?: string;
  };
  organization?: {
    id: number;
    name?: string;
    aiReservationEnabled?: boolean;
    workflowStyle?: string;
  };
};

type DebugLogContextType = {
  logs: DebugLogEntry[];
  addLog: (entry: Omit<DebugLogEntry, 'id' | 'timestamp'>) => void;
  clearLogs: () => void;
  isEnabled: boolean;
  contextInfo: DebugContextInfo;
  setContextInfo: (info: Partial<Omit<DebugContextInfo, 'tripId'>>) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

const DebugLogContext = createContext<DebugLogContextType | null>(null);

const MAX_LOGS = 100;

const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

type DebugLogProviderProps = {
  children: React.ReactNode;
  tripId: string;
  enabled?: boolean;
};

export const DebugLogProvider = ({ children, tripId, enabled = true }: DebugLogProviderProps) => {
  const [logs, setLogs] = useState<DebugLogEntry[]>([]);
  const [contextInfo, setContextInfoState] = useState<DebugContextInfo>({ tripId });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setLogs([]);
    setContextInfoState({ tripId });
  }, [tripId]);

  const addLog = useCallback(
    (entry: Omit<DebugLogEntry, 'id' | 'timestamp'>) => {
      if (!enabled) return;

      const newEntry: DebugLogEntry = {
        ...entry,
        id: generateId(),
        timestamp: new Date()
      };

      setLogs(prev => {
        const updated = [...prev, newEntry];
        if (updated.length > MAX_LOGS) {
          return updated.slice(-MAX_LOGS);
        }
        return updated;
      });
    },
    [enabled]
  );

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const setContextInfo = useCallback((info: Partial<Omit<DebugContextInfo, 'tripId'>>) => {
    setContextInfoState(prev => ({ ...prev, ...info }));
  }, []);

  const value = useMemo(
    () => ({
      logs,
      addLog,
      clearLogs,
      isEnabled: enabled,
      contextInfo,
      setContextInfo,
      isOpen,
      setIsOpen
    }),
    [logs, addLog, clearLogs, enabled, contextInfo, setContextInfo, isOpen]
  );

  return <DebugLogContext.Provider value={value}>{children}</DebugLogContext.Provider>;
};

export const useDebugLogContext = (): DebugLogContextType => {
  const context = useContext(DebugLogContext);
  if (!context) {
    throw new Error('useDebugLogContext must be used within a DebugLogProvider');
  }
  return context;
};

export const useDebugLogContextOptional = (): DebugLogContextType | null => {
  return useContext(DebugLogContext);
};
